/**
 * Parse meeting prep markdown into structured sections.
 *
 * Meeting prep briefs come from two sources with different structures:
 *
 * **AI-generated** (from meeting_prep_service.py):
 *   - ## Context  (optional — only if CRM data exists)
 *   - ## Deals    (optional — only if deals exist)
 *   - ## Prep     (always present)
 *
 * **Demo data** (hardcoded narrative):
 *   - Opening paragraph (bold bottom-line insight, no header)
 *   - ## Talking Points
 *   - ## Risk Signals
 *   - ## Deal Context
 *   - ## Engagement Timeline
 *   - ## Competitive Landscape
 *
 * This parser handles both: it splits by `## ` boundaries and returns
 * an ordered list of {title, content} sections plus any pre-header preamble.
 * The component renders whatever sections exist — no hardcoded expectations.
 */

export interface PrepSection {
  /** Original-case section header (e.g. "Talking Points", "Context", "Prep") */
  title: string;
  /** Section content (everything after the header line, trimmed) */
  content: string;
}

export interface ParsedMeetingPrep {
  /** Text before the first ## header (bold insight / preamble). Empty if markdown starts with ## */
  preamble: string;
  /** Ordered list of sections, preserving document order */
  sections: PrepSection[];
  /** Original markdown */
  raw: string;
}

/**
 * Parse meeting prep markdown into preamble + ordered sections.
 *
 * Defensive: empty input returns empty preamble and no sections.
 */
export function parseMeetingPrepMarkdown(markdown: string): ParsedMeetingPrep {
  if (!markdown) {
    return { preamble: '', sections: [], raw: '' };
  }

  const sections: PrepSection[] = [];
  let preamble = '';

  // Split by ## headers
  const parts = markdown.split(/^## /m);

  // First part is everything before the first ## header
  if (parts[0]) {
    preamble = parts[0].trim();
  }

  // Remaining parts are "Header Name\ncontent..."
  for (let i = 1; i < parts.length; i++) {
    const part = parts[i];
    const newlineIndex = part.indexOf('\n');
    if (newlineIndex === -1) {
      // Header with no content
      sections.push({ title: part.trim(), content: '' });
    } else {
      const title = part.slice(0, newlineIndex).trim();
      const content = part.slice(newlineIndex + 1).trim();
      sections.push({ title, content });
    }
  }

  return { preamble, sections, raw: markdown };
}
